'use client';

import { useState, useEffect, useRef } from 'react';
import { Chrome as Home, Bed, Bath, Users, Baby, MapPin, Clock, Car, Star, Building } from 'lucide-react';

const About = (): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false);
  const [animatedItems, setAnimatedItems] = useState<Set<number>>(new Set());
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; size: number; opacity: number; speed: number; color: string }>>([]);
  const sectionRef = useRef<HTMLElement>(null);
  const particlesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Trigger staggered animations for items
          setTimeout(() => {
            for (let i = 0; i < 15; i++) {
              setTimeout(() => {
                setAnimatedItems(prev => new Set([...prev, i]));
              }, i * 150);
            }
          }, 300);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Initialize magical floating particles
  useEffect(() => {
    const colors = ['#7C8F75', '#A8B89A', '#6B7A64', '#9CAA8F'];
    const initialParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 6 + 2,
      opacity: Math.random() * 0.7 + 0.2,
      speed: Math.random() * 0.8 + 0.3,
      color: colors[Math.floor(Math.random() * colors.length)]
    }));
    setParticles(initialParticles);

    // Animate particles with more dynamic movement
    const animateParticles = () => {
      setParticles(prev => prev.map(particle => ({
        ...particle,
        y: particle.y <= -10 ? 110 : particle.y - particle.speed,
        x: particle.x + Math.sin(Date.now() * 0.001 + particle.id) * 0.5,
        opacity: Math.sin(Date.now() * 0.002 + particle.id) * 0.4 + 0.5,
        size: particle.size + Math.sin(Date.now() * 0.003 + particle.id) * 0.5
      })));
    };

    const interval = setInterval(animateParticles, 60);
    return () => clearInterval(interval);
  }, []);

  const propertyFeatures = [
    { icon: Building, text: "3 Spacious Floors", description: "Multi-level luxury living", gradient: "from-[#7C8F75] to-[#A8B89A]" },
    { icon: Bed, text: "5 Luxurious Bedrooms", description: "Premium sleeping quarters", gradient: "from-[#A8B89A] to-[#7C8F75]" },
    { icon: Bath, text: "3 Full Bathrooms", description: "Modern, well-appointed facilities", gradient: "from-[#7C8F75] via-[#9CAA8F] to-[#A8B89A]" },
    { icon: Users, text: "15 Premium Beds", description: "Accommodates large groups comfortably", gradient: "from-[#A8B89A] via-[#9CAA8F] to-[#7C8F75]" },
    { icon: Baby, text: "2 Cribs Available", description: "Family-friendly amenities", gradient: "from-[#7C8F75] to-[#A8B89A]" },
    { icon: Home, text: "2 Beautiful Porches", description: "Outdoor relaxation spaces", gradient: "from-[#A8B89A] to-[#7C8F75]" },
  ];

  const locationBenefits = [
    { icon: MapPin, text: "On-site Shul & Mikveh", description: "Convenient religious facilities", gradient: "from-[#7C8F75] to-[#A8B89A]" },
    { icon: Clock, text: "4 mins to KJ Munro", description: "Close to community center", gradient: "from-[#A8B89A] to-[#7C8F75]" },
    { icon: Car, text: "7 mins to Woodbury Commons", description: "Premium shopping destination", gradient: "from-[#7C8F75] via-[#9CAA8F] to-[#A8B89A]" },
    { icon: Star, text: "5 mins to רוח החיים Park", description: "Outdoor recreation nearby", gradient: "from-[#A8B89A] via-[#9CAA8F] to-[#7C8F75]" },
  ];

  return (
    <section 
      id="about" 
      ref={sectionRef} 
      className="relative py-20 overflow-hidden"
      style={{
        background: `
          linear-gradient(135deg, #F9F6F2 0%, #E8E3DD 25%, #F9F6F2 50%, #E8E3DD 75%, #F9F6F2 100%),
          radial-gradient(circle at 20% 80%, rgba(124, 143, 117, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(124, 143, 117, 0.12) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(168, 184, 154, 0.08) 0%, transparent 50%)
        `
      }}
    >
      {/* Enhanced Floating Particles */}
      <div ref={particlesRef} className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full animate-pulse-glow"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              backgroundColor: particle.color,
              opacity: particle.opacity,
              transform: `translateZ(0) scale(${1 + Math.sin(Date.now() * 0.001 + particle.id) * 0.2})`,
              boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
              transition: 'all 0.1s ease-out'
            }}
          />
        ))}
      </div>

      {/* Enhanced Decorative Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Animated gradient orbs */}
        <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-gradient-to-br from-[#7C8F75]/20 via-[#A8B89A]/15 to-transparent blur-2xl animate-pulse-glow"></div>
        <div className="absolute bottom-20 right-20 w-60 h-60 rounded-full bg-gradient-to-tl from-[#7C8F75]/15 via-[#9CAA8F]/10 to-transparent blur-3xl animate-float"></div>
        <div className="absolute top-1/2 left-1/4 w-32 h-32 rounded-full bg-gradient-to-r from-[#7C8F75]/18 via-[#A8B89A]/12 to-transparent blur-xl animate-bounce-in"></div>
        
        {/* Geometric patterns with gradients */}
        <div className="absolute top-1/4 right-1/3 w-20 h-20 rotate-45 bg-gradient-to-br from-[#7C8F75]/10 to-[#A8B89A]/5 animate-gradient-shift"></div>
        <div className="absolute bottom-1/3 left-1/5 w-16 h-16 rotate-12 bg-gradient-to-tl from-[#7C8F75]/12 to-[#9CAA8F]/8 rounded-lg animate-float"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className={`text-center mb-16 transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <h2 className="text-4xl md:text-6xl font-bold text-[#333333] mb-6 relative animate-text-glow">
            <span className="bg-gradient-to-r from-[#7C8F75] via-[#A8B89A] to-[#7C8F75] bg-clip-text text-transparent animate-gradient-shift">
              About Mountain View
            </span>
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-[#7C8F75] to-transparent animate-shimmer"></div>
          </h2>
          <p className="text-xl text-[#333333]/80 max-w-3xl mx-auto leading-relaxed">
            Welcome to Mountain View, a beautiful vacation home in Monroe, NY. Our spacious property 
            offers comfortable accommodations with all the amenities your family needs for a wonderful stay.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Image and Location & Amenities */}
          <div className="space-y-8">
            {/* Main Image */}
            <div className="relative overflow-visible mb-16 sm:mb-20">
              <div className="relative overflow-hidden rounded-lg shadow-2xl">
                <img
                  src="https://kujksybmpszaxvhhcyjb.supabase.co/storage/v1/object/public/tmvvilla/shoham-avisrur-FgZIoKBfx08-unsplash%20(1).jpg"
                  alt="Mountain View Villa Exterior"
                  className="w-full h-64 sm:h-80 lg:h-[500px] object-cover transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#7C8F75]/20 to-transparent"></div>
              </div>
              
              {/* Floating Stats */}
              <div className="absolute -bottom-16 right-2 sm:-bottom-16 sm:right-4 lg:-bottom-16 lg:-right-8 bg-white p-3 sm:p-6 rounded-lg shadow-xl border-l-4 border-[#7C8F75] max-w-[120px] sm:max-w-none z-10">
                <div className="text-center">
                  <div className="text-xl sm:text-3xl font-bold text-[#7C8F75]">5★</div>
                  <div className="text-xs sm:text-sm text-[#333333] font-medium">Premium Rating</div>
                </div>
              </div>
            </div>

            {/* Location & Amenities under the image */}
            <div className={`${
              animatedItems.has(1) ? 'animate-slide-in-left' : 'opacity-0'
            }`}>
              <h3 className="text-3xl font-bold text-[#333333] mb-8 relative">
                <span className="bg-gradient-to-r from-[#A8B89A] to-[#7C8F75] bg-clip-text text-transparent">
                  Location & Amenities
                </span>
                <div className="absolute -bottom-2 left-0 w-20 h-1 bg-gradient-to-r from-[#A8B89A] to-[#7C8F75] animate-shimmer"></div>
              </h3>
              
              <div className="space-y-4">
                {locationBenefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className={`group relative overflow-hidden flex items-center space-x-4 p-5 bg-gradient-to-r from-white/90 to-white/70 backdrop-blur-md rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 border border-[#7C8F75]/10 ${
                      animatedItems.has(index + 2) ? 'animate-bounce-in' : 'opacity-0'
                    }`}
                    style={{
                      animationDelay: `${(index + 1) * 100}ms`
                    }}
                  >
                    {/* Gradient overlay on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${benefit.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                    
                    <div className={`relative bg-gradient-to-br ${benefit.gradient} p-3 rounded-xl shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-110`}>
                      <benefit.icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="relative text-lg font-semibold text-[#333333] mb-1 group-hover:text-[#7C8F75] transition-colors duration-300">
                        {benefit.text}
                      </h4>
                      <p className="relative text-sm text-[#333333]/60">
                        {benefit.description}
                      </p>
                    </div>
                    
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Property Features and Additional Images */}
          <div className="space-y-8">
            {/* Property Features */}
            <div className={`${
              animatedItems.has(6) ? 'animate-slide-in-right' : 'opacity-0'
            }`}>
              <h3 className="text-3xl font-bold text-[#333333] mb-8 relative">
                <span className="bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] bg-clip-text text-transparent">
                  Property Features
                </span>
                <div className="absolute -bottom-2 left-0 w-20 h-1 bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] animate-shimmer"></div>
              </h3>
              
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                {propertyFeatures.map((feature, index) => (
                  <div 
                    key={index}
                    className={`group relative overflow-hidden flex items-center space-x-3 p-3 sm:p-5 bg-gradient-to-r from-white/90 to-white/70 backdrop-blur-md rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 border border-[#7C8F75]/10 ${
                      animatedItems.has(index + 7) ? 'animate-bounce-in' : 'opacity-0'
                    }`}
                    style={{
                      animationDelay: `${(index + 6) * 100}ms`
                    }}
                  >
                    {/* Gradient overlay on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                    
                    <div className={`relative bg-gradient-to-br ${feature.gradient} p-3 rounded-xl shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-110`}>
                      <feature.icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </div>
                    <div>
                      <span className="relative text-[#333333] font-semibold text-xs sm:text-sm group-hover:text-[#7C8F75] transition-colors duration-300">
                        {feature.text}
                      </span>
                    </div>
                    
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Images */}
            <div className="space-y-6">
              {/* Interior Image */}
              <div className={`relative overflow-hidden rounded-2xl shadow-xl group ${
                animatedItems.has(13) ? 'animate-slide-in-right' : 'opacity-0'
              }`}>
                <img
                  src="https://kujksybmpszaxvhhcyjb.supabase.co/storage/v1/object/public/tmvvilla/ahmad-ajmi-2Wqit9jCNeY-unsplash.jpg"
                  alt="Luxury Living Room Interior"
                  className="w-full h-[200px] sm:h-[250px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 text-white">
                  <h4 className="text-base sm:text-lg font-semibold">Elegant Living Spaces</h4>
                  <p className="text-sm opacity-90">Sophisticated interior design</p>
                </div>
              </div>

              {/* Bedroom Image */}
              <div className={`relative overflow-hidden rounded-2xl shadow-xl group ${
                animatedItems.has(14) ? 'animate-slide-in-right' : 'opacity-0'
              }`}>
                <img
                  src="https://inweleursyulyllmzoca.supabase.co/storage/v1/object/public/moiuntainview//Annotation%202021-06-22%20170458%2026.png"
                  alt="Luxurious Master Bedroom"
                  className="w-full h-[200px] sm:h-[250px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 text-white">
                  <h4 className="text-base sm:text-lg font-semibold">Premium Bedrooms</h4>
                  <p className="text-sm opacity-90">Comfort meets luxury</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Feature Section */}
        <div className={`mt-12 sm:mt-16 mb-16 sm:mb-32 relative overflow-hidden bg-gradient-to-br from-[#7C8F75]/15 via-[#A8B89A]/10 to-[#7C8F75]/5 p-6 sm:p-8 rounded-2xl border border-[#7C8F75]/20 backdrop-blur-md shadow-xl ${
          animatedItems.has(12) ? 'animate-bounce-in' : 'opacity-0'
        }`}>
          {/* Animated background pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#7C8F75]/5 via-transparent to-[#A8B89A]/5 animate-gradient-shift"></div>
          
          <h4 className="relative text-xl sm:text-2xl font-bold text-[#333333] mb-4 flex items-center">
            <span className="bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] bg-clip-text text-transparent">
              About the Property
            </span>
          </h4>
          <p className="relative text-[#333333]/80 leading-relaxed text-base sm:text-lg">
            Mountain View is a well-maintained property featuring beautiful outdoor spaces and gardens. 
            The villa provides a comfortable environment for families seeking a peaceful retreat 
            in Monroe with easy access to local amenities and attractions.
          </p>
          
          {/* Floating decorative elements */}
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-[#7C8F75]/20 to-[#A8B89A]/20 rounded-full animate-float"></div>
          <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-[#A8B89A]/20 to-[#7C8F75]/20 rounded-full animate-bounce"></div>
        </div>
      </div>

      {/* Enhanced Wave transition to Gallery section */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
        <svg 
          className="relative block w-full h-32" 
          data-name="Layer 1" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="waveGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#F9F6F2" stopOpacity="1" />
              <stop offset="20%" stopColor="#E8E3DD" stopOpacity="0.9" />
              <stop offset="40%" stopColor="#F5F5F5" stopOpacity="0.7" />
              <stop offset="60%" stopColor="#FAFAFA" stopOpacity="0.5" />
              <stop offset="80%" stopColor="#FDFDFD" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#ffffff" />
            </linearGradient>
            <linearGradient id="waveGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(124, 143, 117, 0.06)" stopOpacity="0.8" />
              <stop offset="30%" stopColor="rgba(168, 184, 154, 0.03)" stopOpacity="0.6" />
              <stop offset="60%" stopColor="rgba(255, 255, 255, 0.8)" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="waveGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(249, 246, 242, 0.4)" stopOpacity="0.6" />
              <stop offset="50%" stopColor="rgba(255, 255, 255, 0.9)" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#ffffff" />
            </linearGradient>
            <linearGradient id="waveGradient4" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.3)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="1" />
            </linearGradient>
          </defs>
          
          {/* Base wave with main gradient */}
          <path 
            d="M0,60L48,65C96,70,192,80,288,75C384,70,480,50,576,45C672,40,768,50,864,60C960,70,1056,80,1152,75C1200,70,1248,50,1296,45L1344,40L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#waveGradient1)"
          />
          
          {/* Second wave layer */}
          <path 
            d="M0,40L48,45C96,50,192,60,288,55C384,50,480,30,576,25C672,20,768,30,864,40C960,50,1056,60,1152,55C1200,50,1248,30,1296,25L1344,20L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#waveGradient2)"
          />
          
          {/* Third wave layer */}
          <path 
            d="M0,20L48,25C96,30,192,40,288,35C384,30,480,10,576,5C672,0,768,10,864,20C960,30,1056,40,1152,35C1200,30,1248,10,1296,5L1344,0L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#waveGradient3)"
          />
          
          {/* Final top wave for perfect white transition */}
          <path 
            d="M0,0L48,5C96,10,192,20,288,15C384,10,480,-10,576,-15C672,-20,768,-10,864,0C960,10,1056,20,1152,15C1200,10,1248,-10,1296,-15L1344,-20L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#waveGradient4)"
          />
        </svg>
      </div>
    </section>
  );
};

export default About;